import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import TextAreaInput from '../../../../../../../../../../components/forms/text-area-input/TextAreaInput';
import TextInput from '../../../../../../../../../../components/forms/text-input/TextInput';
import { useUpdateCalculation } from '../../../../../../../../../../hooks/api/footprints';
import { useFootprintAndProduct } from '../../../../../../../../../../hooks/useFootprintAndProduct';
import { PROCESS_DROPDOWN_OPTIONS, ProcessIdsMap } from '../../PcfWizard.type';
import BiogenicRemovalCard from './components/cards/BiogenicRemovalCard';
import ElectricityCard from './components/cards/ElectricityCard';
import GeneralInformationCard from './components/cards/GeneralInformationCard';
import IndividualProcessCard from './components/cards/IndividualProcessCard';
import InjectionMoldingProcessCard from './components/cards/InjectionMoldingProcessCard';
import TransportProcessCard from './components/cards/TransportProcessCard';
import EmissionBreakdownSection from './components/EmissionBreakdownSection';

type CalculationDetailsFormData = {
  name: string;
  pcfLegalStatement: string | undefined;
};

const CalculationDetails = (): JSX.Element => {
  const { product, footprintDetails, calculationDetails } = useFootprintAndProduct();
  const { t } = useTranslation();

  const navigate = useNavigate();

  // Hardcoded values for EmissionsBreakdown for now

  const queryClient = useQueryClient();

  const onSuccessSaveName = () => {
    queryClient.invalidateQueries({
      queryKey: ['footprints', 'calculations', footprintDetails?.id],
    });

    queryClient.invalidateQueries({
      queryKey: ['footprints', 'calculation', calculationDetails?.id],
    });

    // Navigate to the calculations overview page
    const href = `/products/${product?.id}/pcf/footprint/${footprintDetails?.id}/calculations`;
    navigate(href);
  };

  const { mutate: updateCalculationName, isLoading: isUpdating } = useUpdateCalculation({
    onSuccess: onSuccessSaveName,
  });

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t('calculationDetailsPage.inputs.validationErrors.pcfCalculationNameRequired')),
    pcfLegalStatement: yup.string().optional(),
  });

  const defaultValues: CalculationDetailsFormData = {
    name: calculationDetails?.name || '',
    // eslint-disable-next-line camelcase
    pcfLegalStatement: calculationDetails?.data?.pcf_legal_statement || '',
  };

  const methods = useForm<CalculationDetailsFormData>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const { control, handleSubmit } = methods;

  const onClickSave = (data: CalculationDetailsFormData) => {
    if (calculationDetails?.id && footprintDetails?.id) {
      updateCalculationName({
        calculationId: calculationDetails.id,
        footprintId: footprintDetails.id,
        name: data.name,
        // eslint-disable-next-line camelcase
        pcf_legal_statement: data.pcfLegalStatement,
      });
    }
  };

  const PROCESS_COMPONENT_MAP = {
    [ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]]: InjectionMoldingProcessCard,

    [ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]]: IndividualProcessCard,

    [ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]]: TransportProcessCard,
  };

  return (
    <div className='flex w-full justify-content-center'>
      <div className='flex flex-column gap-4 align-items-center w-full md:w-10 lg:w-8 xl:w-7'>
        <div className='bg-white px-5 py-4 border-round-2xl flex flex-column gap-4 w-full'>
          {/* Title */}
          <h1 className='text-xl md:text-2xl font-medium p-0 mb-2'>
            {t('calculationDetailsPage.title')}
          </h1>

          {/* Calculation Name Input */}
          <TextInput
            name='name'
            control={control}
            label={t('calculationDetailsPage.inputs.placeholders.pcfCalculationName')}
          />

          {/* PCF Section */}
          <div className='flex flex-column gap-2 mb-2'>
            <h5 className='p-0 m-0 font-medium'>
              {t('calculationDetailsPage.sections.pcf.title')}
            </h5>
            <div className='flex flex-row gap-4 w-full'>
              <BiogenicRemovalCard
                title={t('calculationDetailsPage.sections.pcf.exclBiogenicEmissionsAndRemovals')}
                value={calculationDetails?.pcfExcludingBiogenic || 0}
                declaredUnit={calculationDetails?.data?.declared_unit}
                declaredAmount={calculationDetails?.data?.unitary_product_amount}
              />

              <BiogenicRemovalCard
                title={t('calculationDetailsPage.sections.pcf.inclBiogenicEmissionsAndRemovals')}
                value={calculationDetails?.pcfIncludingBiogenic || 0}
                declaredUnit={calculationDetails?.data?.declared_unit}
                declaredAmount={calculationDetails?.data?.unitary_product_amount}
              />
            </div>
          </div>

          {/* Emissions Breakdown Section */}
          <EmissionBreakdownSection calculationResponse={calculationDetails} />

          {/* General Information Section */}
          <GeneralInformationCard calculationDetails={calculationDetails?.data} />

          {/* Electricity Section */}
          <ElectricityCard calculationDetails={calculationDetails?.data} />

          {/* Process Cards */}
          {calculationDetails?.data?.processes?.map((process, index) => {
            const Component = PROCESS_COMPONENT_MAP[process?.process_id];

            return Component ? <Component key={index} process={process} /> : null;
          })}

          {/* Additional Details Section */}
          <div className='flex flex-column gap-2 w-full'>
            <h5 className='p-0 m-0 font-medium'>
              {t('calculationDetailsPage.sections.additionalDetails.title')}
            </h5>
            <TextAreaInput
              name='pcfLegalStatement'
              label={t('calculationDetailsPage.sections.additionalDetails.disclaimer')}
              control={control}
              rows={6}
              className='w-full'
            />
          </div>
        </div>

        {/* Save button */}
        <div className='flex w-full justify-content-end'>
          <Button
            label={t('calculationDetailsPage.buttons.save')}
            onClick={handleSubmit(onClickSave)}
            loading={isUpdating}
          />
        </div>
      </div>
    </div>
  );
};

export default CalculationDetails;
